import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  Paper, IconButton, Button, Typography, Chip,
  CircularProgress, Switch, Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import PeopleIcon from "@material-ui/icons/People";
import { toast } from "react-toastify";

import { listPopups, deletePopup, updatePopup, resetDismissals } from "../../services/companyPopupService";
import CompanyPopupFormModal from "../../components/CompanyPopupFormModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    width: "100%",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  pageHeader: { marginBottom: theme.spacing(3) },
  pageTitle: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a2e",
    marginBottom: theme.spacing(0.5),
  },
  pageSubtitle: { fontSize: "14px", color: "#6b7280" },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fff",
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3),
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    flexWrap: "wrap",
    gap: 8,
  },
  content: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  tableHead: {
    "& th": {
      backgroundColor: "var(--sidebar-color, #1e293b)",
      color: "#fff",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      padding: "10px 16px",
    },
  },
  typeChip: { fontSize: "0.7rem", fontWeight: 700 },
  filterSummary: {
    fontSize: "0.75rem",
    color: "#64748b",
    maxWidth: 280,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  actionBar: { display: "flex", gap: 4 },
}));

const TYPE_COLORS = {
  info: "primary",
  warning: "default",
  success: "default",
  error: "default",
  announcement: "secondary",
};

const TYPE_LABELS = {
  info: "Informação",
  warning: "Aviso",
  success: "Sucesso",
  error: "Alerta",
  announcement: "Anúncio",
};

const TYPE_STYLES = {
  info: { background: "#e0f2fe", color: "#0369a1" },
  warning: { background: "#fef9c3", color: "#92400e" },
  success: { background: "#dcfce7", color: "#166534" },
  error: { background: "#fee2e2", color: "#991b1b" },
  announcement: { background: "#f3e8ff", color: "#7c3aed" },
};

const summarizeFilters = (filters) => {
  if (!filters || !filters.conditions || filters.conditions.length === 0) return "Todas as empresas";
  const op = filters.operator === "OR" ? " OU " : " E ";
  return filters.conditions.map((c) => {
    switch (c.type) {
      case "all_companies": return "Todas";
      case "status_active": return "Ativas";
      case "status_inactive": return "Inativas";
      case "trial": return "Em teste";
      case "trial_days_min": return `Teste > ${c.value}d`;
      case "trial_days_max": return `Teste < ${c.value}d`;
      case "registered_days_max": return `Novas < ${c.value}d`;
      case "registered_days_min": return `Cadastro > ${c.value}d`;
      case "active_days_min": return `Ativas > ${c.value}d`;
      case "overdue_invoice_days_min": return `Fatura vencida > ${c.value}d`;
      case "due_date_expiring_days": return `Vence em ${c.value}d`;
      case "due_date_expired": return "Vencimento expirado";
      case "no_login_days_min": return `Sem login > ${c.value}d`;
      case "plan_id": return `Plano #${c.value}`;
      case "segment": return `Segmento: ${c.value}`;
      case "specific_companies": return `IDs: ${Array.isArray(c.value) ? c.value.join(",") : c.value}`;
      default: return c.type;
    }
  }).join(op);
};

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const CompanyPopupsAdmin = () => {
  const classes = useStyles();

  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  const sidebarColor = (() => {
    try {
      const t = window.__MUI_THEME__ || {};
      return t.palette?.primary?.main || "#1e293b";
    } catch { return "#1e293b"; }
  })();

  const load = useCallback(() => {
    setLoading(true);
    listPopups()
      .then(({ data }) => setPopups(data))
      .catch(() => toast.error("Erro ao carregar pop-ups"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleActive = async (popup) => {
    try {
      await updatePopup(popup.id, { ...popup, active: !popup.active });
      setPopups((prev) => prev.map((p) => p.id === popup.id ? { ...p, active: !p.active } : p));
    } catch {
      toast.error("Erro ao alterar status");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePopup(deleteTarget.id);
      toast.success("Pop-up excluído");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const handleResetDismissals = async (id) => {
    setResettingId(id);
    try {
      await resetDismissals(id);
      toast.success("Dispensas resetadas — todas as empresas verão o pop-up novamente");
    } catch {
      toast.error("Erro ao resetar");
    } finally {
      setResettingId(null);
    }
  };

  return (
    <div className={classes.root} style={{ "--sidebar-color": sidebarColor }}>
      <ConfirmationModal
        title="Excluir pop-up"
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      >
        Excluir o pop-up <strong>"{deleteTarget?.title}"</strong>? Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <CompanyPopupFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        item={selected}
        onSaved={load}
      />

      <div className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>Pop-ups para Empresas</Typography>
        <Typography className={classes.pageSubtitle}>
          Crie comunicados segmentados que aparecem automaticamente para as empresas clientes
        </Typography>
      </div>

      <div className={classes.toolbarRow}>
        <Typography variant="body2" style={{ color: "#64748b" }}>
          {popups.length} pop-up(s) cadastrado(s)
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          style={{ textTransform: "none", borderRadius: 8, fontWeight: 600 }}
          onClick={() => { setSelected(null); setFormOpen(true); }}
        >
          Novo Pop-up
        </Button>
      </div>

      <Paper className={classes.content}>
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>Título</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Filtros</TableCell>
              <TableCell>Início</TableCell>
              <TableCell>Fim</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={7} />
            ) : popups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: "32px 0", color: "#94a3b8" }}>
                  Nenhum pop-up criado ainda
                </TableCell>
              </TableRow>
            ) : (
              popups.map((popup) => (
                <TableRow key={popup.id} hover>
                  <TableCell>
                    <Typography style={{ fontWeight: 600, fontSize: "0.875rem" }}>{popup.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <span style={{
                      ...TYPE_STYLES[popup.type] || TYPE_STYLES.info,
                      padding: "2px 10px",
                      borderRadius: 12,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      display: "inline-block",
                    }}>
                      {TYPE_LABELS[popup.type] || popup.type}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.filterSummary} title={summarizeFilters(popup.filters)}>
                      {summarizeFilters(popup.filters)}
                    </span>
                  </TableCell>
                  <TableCell>{formatDate(popup.startDate)}</TableCell>
                  <TableCell>{formatDate(popup.endDate)}</TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={popup.active}
                      onChange={() => handleToggleActive(popup)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.actionBar}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => { setSelected(popup); setFormOpen(true); }}>
                          <EditIcon fontSize="small" style={{ color: "#3b82f6" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Resetar dispensas (mostrar novamente para todos)">
                        <IconButton
                          size="small"
                          onClick={() => handleResetDismissals(popup.id)}
                          disabled={resettingId === popup.id}
                        >
                          {resettingId === popup.id
                            ? <CircularProgress size={14} />
                            : <RefreshIcon fontSize="small" style={{ color: "#8b5cf6" }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => setDeleteTarget(popup)}>
                          <DeleteIcon fontSize="small" style={{ color: "#ef4444" }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};

export default CompanyPopupsAdmin;
